import {
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import qs from "qs";
import { ISbStoryData, useStoryblokBridge } from "@storyblok/react";
import { useSearchParams } from "react-router-dom";
import { CONFIG } from "@lib/config";
import { apiClient } from "@lib/api";
import { useMemo } from "react";
import { UseFetchOptions } from "../types/StoryblokTypes";
import { AxiosHeaders, AxiosResponseHeaders } from "axios";
import {
  getEditorTokenParams,
  isStoryblokEditor,
} from "@utils/storyblokEditor";

export const ERROR_MESSAGE = "Failed to fetch stories response";
const API_CONTENT_ROUTE = "/api/v1/cdn/stories";
const DRAFT_CONTENT_STAGE = "draft";

interface StoryblokResponse {
  stories?: ISbStoryData[];
  rels?: ISbStoryData[];
  story?: ISbStoryData;
  headers: AxiosResponseHeaders;
}

/**
 * Custom hook to fetch stories from Storyblok using a proxy.
 *
 * @param {UseFetchOptions} args - The options for fetching data.
 * @param {boolean} [abortRequest] - Optional flag to abort the request.
 * @returns {UseQueryResult<ISbStoryData[]>} - The result of the query.
 *
 * @example
 * const { data, error, isLoading } = useStoryblokProxy({
 *   unique_identifier: 'example_id',
 *   starts_with: 'example_start',
 *   slug: 'example-slug',
 *   otherOption: 'value'
 * });
 */
export const useStoryblokProxy = (
  args: UseFetchOptions,
  abortRequest?: boolean
): UseQueryResult<StoryblokResponse> => {
  const { unique_identifier, starts_with, slug, ...rest } = args;
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  // When previewed inside the Storyblok Visual Editor, request draft content and forward the
  // signed `_storyblok_tk` token so the API can validate the editor session server-side.
  const editor = isStoryblokEditor(searchParams);
  const editorTokenParams = useMemo(
    () => (editor ? getEditorTokenParams(searchParams) : {}),
    [editor, searchParams]
  );
  const version = editor ? DRAFT_CONTENT_STAGE : CONFIG.STORYBLOK_CONTENT_STAGE;

  /**
   * Memoized query string parameters including additional configuration for Storyblok content stage.
   *
   * @constant {string} params - The memoized query string parameters.
   * @returns {string} The query string representation of the parameters.
   */
  const params = useMemo(
    () =>
      qs.stringify({
        ...rest,
        starts_with,
        ...editorTokenParams,
        page: rest.page ?? 1,
        version,
      }),
    [rest, starts_with, editorTokenParams, version]
  );

  /**
   * Fetches stories from the Storyblok API.
   *
   * @returns {Promise<StoryblokResponse>} A promise that resolves to the response data from the Storyblok API.
   * @throws Will throw an error if the request fails.
   */
  const fetchStories = async (): Promise<StoryblokResponse> => {
    try {
      if (slug) {
        // Build the query-string for a single‑story request, preserving optional language, etc.
        const slugParams = qs.stringify({
          ...rest,
          ...editorTokenParams,
          version,
        });

        const url = `${API_CONTENT_ROUTE}/${typeof slug === "string" ? slug : ""}?${slugParams}`;

        // If a slug is provided, fetch a single story by slug
        const response = await apiClient.get<{ story: ISbStoryData }>(url);

        // Return the story data along with headers
        return {
          ...response.data,
          headers: response.headers as AxiosHeaders,
        };
      } else {
        const url = `${API_CONTENT_ROUTE}?${params}`;

        // If no slug is provided, fetch multiple stories based on unique_identifier or starts_with
        const response = await apiClient.get<StoryblokResponse>(url);

        // Return the stories data along with headers
        return {
          ...response.data,
          headers: response.headers as AxiosHeaders,
        };
      }
    } catch (error) {
      console.error(ERROR_MESSAGE, error);
      throw new Error(ERROR_MESSAGE);
    }
  };

  const queryKey = [
    "pulse",
    slug ?? starts_with ?? "",
    params,
    unique_identifier ?? "",
  ];

  const query = useQuery<StoryblokResponse, Error, StoryblokResponse>({
    refetchOnWindowFocus: false,
    queryKey,
    queryFn: fetchStories,
    enabled: !abortRequest,
    select: (data) => data,
  });

  // In the Storyblok Visual Editor, subscribe to live edits and update the cached raw story so
  // consumers (and any downstream transforms) re-render with the editor's changes. With id 0
  // (not in the editor, or no single story loaded) the bridge is a no-op.
  const liveStoryId = editor ? (query.data?.story?.id ?? 0) : 0;
  useStoryblokBridge(liveStoryId, (updatedStory) => {
    queryClient.setQueryData<StoryblokResponse>(queryKey, (previous) =>
      previous ? { ...previous, story: updatedStory } : previous
    );
  });

  return query;
};
